"use client";

import { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { showToast } from "../utils/toastUtils";
import {
  Trash2,
  Eye,
  EyeOff,
  User,
  Mail,
  Shield,
  Plus,
  Search,
  X,
  Lock
} from "lucide-react";

function AddUsers() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const BASE_URL = process.env.REACT_APP_API_URL;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/roles`);
      setRoles(res.data);
    } catch (err) {
      showToast("Error", "Failed to load roles", "error");
    }
  }, [BASE_URL]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/auth`);
      setUsers(res.data);
    } catch (err) {
      showToast("Error", "Failed to load users", "error");
    } finally {
      setLoading(false);
    }
  }, [BASE_URL]);

  useEffect(() => {
    fetchRoles();
    fetchUsers();
  }, [fetchRoles, fetchUsers]);

  const handleAddUser = async () => {
    if (!username || !email || !password || !selectedRole) {
      showToast("Validation Error", "All fields are required", "error");
      return;
    }

    try {
      const res = await axios.post(`${BASE_URL}/api/auth/users`, {
        username,
        email,
        password,
        role: selectedRole,
      });
      showToast("Success", "User created successfully", "success");
      setUsers([...users, res.data.user]);
      resetForm();
      setShowModal(false);
    } catch (err) {
      showToast("Error", err.response?.data?.message || "Failed to create user", "error");
    }
  };

  const resetForm = () => {
    setUsername("");
    setEmail("");
    setPassword("");
    setSelectedRole("");
    setShowPassword(false);
  }

  const confirmDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await axios.delete(`${BASE_URL}/api/auth/users/${userToDelete._id}`);
      setUsers(users.filter((u) => u._id !== userToDelete._id));
      showToast("Success", "User deleted successfully", "success");
    } catch (err) {
      showToast("Error", "Failed to delete user", "error");
    } finally {
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;
    return users.filter(u =>
      u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [users, searchTerm]);

  return (
    <div className="flex bg-gray-50 min-h-screen pb-12">
      <div className="flex-1 flex flex-col w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <User className="w-8 h-8 text-blue-600" /> Team Members
            </h1>
            <p className="mt-2 text-sm text-gray-500">
              Manage your team's access and roles.
            </p>
          </div>
          {/* Add Button (Mobile) */}
          <button
            onClick={() => setShowModal(true)}
            className="md:hidden flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 shadow-sm transition-all"
          >
            <Plus className="w-5 h-5" /> Add New User
          </button>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-8">
          <div className="relative group max-w-lg">
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            <input
              type="text"
              placeholder="Search users by name or email..."
              className="w-full rounded-lg border-gray-200 bg-white px-4 py-2.5 pl-10 text-gray-700 placeholder-gray-400 shadow-sm transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none hover:border-blue-300"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Grid Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">

          {/* Create New User Card */}
          <div
            onClick={() => setShowModal(true)}
            className="hidden md:flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-8 cursor-pointer hover:border-blue-500 hover:bg-blue-50/50 transition-all text-gray-500 hover:text-blue-600 group h-full min-h-[220px]"
          >
            <div className="h-14 w-14 bg-gray-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-100 transition-colors">
              <Plus className="h-8 w-8 text-gray-400 group-hover:text-blue-600" />
            </div>
            <h3 className="text-lg font-medium">Add Team Member</h3>
            <p className="text-sm text-center mt-2 px-4">Invite a new user to the dashboard.</p>
          </div>

          {loading ? (
            [1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 h-64 animate-pulse">
                <div className="flex items-center gap-4 mb-4">
                  <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
                  <div className="space-y-2 flex-1">
                    <div className="h-4 bg-gray-100 rounded w-1/2"></div>
                    <div className="h-3 bg-gray-100 rounded w-3/4"></div>
                  </div>
                </div>
                <div className="h-4 bg-gray-100 rounded w-full mt-4"></div>
              </div>
            ))
          ) : filteredUsers.length === 0 ? (
            <div className="col-span-full text-center py-12 text-gray-500">
              No users found matching your search.
            </div>
          ) : (
            filteredUsers.map((user) => (
              <div key={user._id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all flex flex-col h-full relative group">
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => { e.stopPropagation(); confirmDeleteUser(user); }}
                    className="text-gray-400 hover:text-red-600 hover:bg-red-50 p-2 rounded-lg transition-colors"
                    title="Delete User"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-6 flex flex-col items-center text-center flex-1">
                  <div className="h-20 w-20 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 flex items-center justify-center text-blue-600 mb-4 shadow-sm">
                    <span className="text-2xl font-bold">{user.username.charAt(0).toUpperCase()}</span>
                  </div>

                  <h3 className="text-lg font-bold text-gray-900 mb-1">{user.username}</h3>
                  <a href={`mailto:${user.email}`} className="text-sm text-gray-500 hover:text-blue-600 flex items-center gap-1.5 mb-4">
                    <Mail className="w-3.5 h-3.5" />
                    {user.email}
                  </a>

                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                    <Shield className="w-3 h-3 mr-1" />
                    {user.role}
                  </span>
                </div>
                <div className="bg-gray-50 px-6 py-3 border-t border-gray-100 text-center">
                  <span className="text-xs text-gray-400">ID: <span className="font-mono">{user._id.slice(-6)}</span></span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add User Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-in fade-in zoom-in duration-200">
              <div className="flex justify-between items-center p-6 border-b">
                <h3 className="text-lg font-bold text-gray-900">Create New User</h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-500">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                  <div className="relative">
                    <User className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="e.g. jdoe"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="w-full rounded-lg border-gray-300 pl-10 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      placeholder="john@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full rounded-lg border-gray-300 pl-10 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full rounded-lg border-gray-300 pl-10 pr-10 focus:border-blue-500 focus:ring-blue-500"
                    />
                    <div
                      className="absolute inset-y-0 right-3 flex items-center cursor-pointer text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="w-full rounded-lg border-gray-300 pl-10 focus:border-blue-500 focus:ring-blue-500 appearance-none bg-white"
                    >
                      <option value="">Select a role...</option>
                      {roles.map((role) => (
                        <option key={role._id} value={role.roleName}>
                          {role.roleName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t bg-gray-50 flex justify-end gap-3 rounded-b-xl">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-300 bg-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddUser}
                  className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors"
                >
                  Create User
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 animate-in fade-in zoom-in duration-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">Remove User?</h2>
                <button onClick={() => setShowDeleteModal(false)} className="text-gray-400 hover:text-gray-500">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                Are you sure you want to remove <strong>{userToDelete?.username}</strong> from the team?
                They will lose access immediately.
              </p>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 shadow-sm transition-all"
                >
                  Remove User
                </button>
              </div>
            </div>
          </div>
        )}


      </div>
    </div>
  );
}

export default AddUsers;
